import { useEffect, useState, useRef } from 'react'
import api from 'config/axiosConfig'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import ImpactCardRefactored from '../ImpactCardRefactored'
import TeabateCard from '../../Teabates/TeabateCard'
import { EntityCardRef } from 'components/Entities/EntityCardBase'
import ImpactProfileTabs from './ImpactProfileTabs'
import { Impact } from 'util/constants'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'
import AlignmentModal from 'components/Alignment/AlignmentModal'
import { apiRoutes } from "util/apiRoutes";
import { applyPendingMediaOrder, syncPostCoauthors } from 'components/Posts/display/postProfileHelpers'
import { usePostProfileController } from 'components/Posts/display/usePostProfileController'
import {
  normalizeExistingCoauthors,
  usePostCoauthorState,
} from 'components/Posts/display/usePostCoauthorState'

const ImpactProfile = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [description, setDescription] = useState<string>('')
  const [privacy, setPrivacy] = useState<string>('public')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const {
    entity: impact,
    setEntity: setImpact,
    error,
    isLoading,
    editMode,
    setEditMode,
    setSearchParams,
    resolvedKind,
  } = usePostProfileController<Impact>({
    id,
    fallbackKind: "impact",
    getResolvedKind: (entity) => (entity.kind === 'teabate' ? 'teabate' : 'impact'),
    getDocumentTitle: (entity) =>
      `${entity?.kind === 'teabate' ? 'Teabate' : 'Impact'} - ${entity?.title || ''}`,
    notFoundError: '404: Failed to load impact profile. Please try again later.',
    genericError: 'Failed to load impact profile. Please try again later.',
  })
  const impactCardRef = useRef<EntityCardRef>(null)
  const location = useLocation();
  const [showAlignmentModal, setShowAlignmentModal] = useState(false);
  const [alignmentArgumentType, setAlignmentArgumentType] = useState<'agree' | 'disagree'>('agree');
  const {
    coauthors,
    newCoauthors,
    removedCoauthors,
    initializeCoauthors,
    handleCoauthorsChange,
    resetCoauthorChanges,
  } = usePostCoauthorState(privacy)

  useEffect(() => {
    if (location.state?.openAlignmentModal) {
      setShowAlignmentModal(true);
      setAlignmentArgumentType(location.state.argumentType || 'agree');
      
      // Clear the navigation state to prevent modal from reopening on refresh
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname, location.search]);

  useEffect(() => {
    if (!impact) {
      return
    }

    setDescription(impact.body || "")
    setPrivacy(impact.properties?.privacy || 'public')
    initializeCoauthors(normalizeExistingCoauthors(impact.properties?.coauthors))
  }, [impact, initializeCoauthors])

  const handleSave = async () => {
    if (isSubmitting) return

    setIsSubmitting(true)
    try {
      const mediaOrderSuccess = await applyPendingMediaOrder(impactCardRef)
      if (!mediaOrderSuccess) {
        return
      }

      await api.patch(apiRoutes.posts.byId(id || ""), {
        body: description,
        privacy
      })

      await syncPostCoauthors(resolvedKind, id || "", newCoauthors, removedCoauthors, privacy)

      setImpact(prev => prev ? {
        ...prev,
        body: description,
        properties: {
          ...prev.properties,
          privacy,
          coauthors: [...coauthors]
        }
      } : null)
      setEditMode(false)
      resetCoauthorChanges()
      setSearchParams({})
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (impact) {
      setDescription(impact.body || "")
      setPrivacy(impact.properties?.privacy || "public")
      initializeCoauthors(normalizeExistingCoauthors(impact.properties?.coauthors))
    }
    setEditMode(false)
    setSearchParams({})
  }

  if (error) {
    return error.match(/404/) ? 
      <NotFoundPage 
        displayStyle="page" 
        errorMessage="Sorry, we couldn't find this impact." 
        errorSubtext="It may be only visible to specific users. Double check the URL or try reloading the page." 
        buttonText="See Other Impacts" 
        buttonHref="/impacts/new" 
      /> : 
      <ServerErrorPage />
  }

  if (isLoading || !impact) {
    return <LoadingSkeleton count={1} variant="entity-card" margin="mt-4" />
  }

  const editorButtons = editMode ? (
    <div className="flex justify-end gap-2">
      <button
        onClick={handleCancel}
        className="btn-secondary py-1.5"
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        onClick={handleSave}
        className={isSubmitting ? "btn-primary-disabled py-1.5" : "btn-primary py-1.5"}
        disabled={isSubmitting}
      >
        Save
      </button>
    </div>
  ) : null;

  return (
    <div className="py-0 relative">
      <div>
        {impact && (
          <>
            {impact.kind === 'teabate' ? (
              <TeabateCard
                ref={impactCardRef}
                teabate={{...impact, properties: {...impact.properties, privacy}}}
                truncateDescription={true}
                isEntityProfilePage={true}
                isEntitySection={false}
                editMode={editMode}
                onDescriptionChange={setDescription}
                onPrivacyChange={setPrivacy}
                onCoauthorsChange={handleCoauthorsChange}
              >
                {editorButtons}
              </TeabateCard>
            ) : (
              <ImpactCardRefactored
                ref={impactCardRef}
                impact={{...impact, properties: {...impact.properties, privacy}}}
                truncateDescription={true}
                isEntityProfilePage={true}
                isEntitySection={false}
                editMode={editMode}
                onDescriptionChange={setDescription}
                onPrivacyChange={setPrivacy}
                onCoauthorsChange={handleCoauthorsChange}
              >
                {editorButtons}
              </ImpactCardRefactored>
            )}
          </>
        )}
      </div>
      <ImpactProfileTabs impact={impact} />
       {showAlignmentModal && impact && (
        <AlignmentModal
          isOpen={showAlignmentModal}
          onClose={() => setShowAlignmentModal(false)}
          teabate={impact}
          stance={alignmentArgumentType}
          onOpenArgumentModal={() => {
            setShowAlignmentModal(false);
          }}
        />
      )}
    </div>
  )
}

export default ImpactProfile
